/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import AddEmailModal from '@brands/Dashboard/Dashboard/SuperAdmin/Components/AddEmailModal/AddEmailModal';
import { useLocalStorageState } from '@brands/hooks';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { activateUserById } from '@brands/services/identity/activateUserById';
import { confirmAvatarMedia } from '@brands/services/identity/confirmAvatarMedia';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { registerAvatarMedia } from '@brands/services/identity/registerAvatarMedia';
import { Organization, UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { setSelectedPatient } from '@brands/store/slices/patientFormSlice';
import { setSelectedPatientByAdmin } from '@brands/store/slices/selectedPatientSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { heicTo } from 'heic-to';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsPerson } from 'react-icons/bs';
import { FaSpinner } from 'react-icons/fa';
import { FiEdit } from 'react-icons/fi';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { MdKeyboardArrowLeft, MdOutlineChangeCircle } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import Button from '../../../Components/Button/Button';
import { reinviteUserById } from '../../../services/identity/reinviteUserById';
import resetPasswordIcon from '../../ConsultationPage/assets/icons/resetPassword.svg';
import createCaseIcon from './assets/createCase.svg';
import styles from './styles.module.scss';

interface ReviewProfileHeaderProps {
  thisUser: UserProfile;
  setThisUser: React.Dispatch<React.SetStateAction<UserProfile>>;
  setResetPasswordConfirmation: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      userState: string;
    }>
  >;
  resetEmailSent: boolean;
  setChangeUserStatusFlag: React.Dispatch<React.SetStateAction<string>>;
  userId?: string;
  userRole: string;
  userOrganizations: Organization[];
}

const ReviewProfileHeader: React.FC<ReviewProfileHeaderProps> = ({
  thisUser,
  setThisUser,
  setResetPasswordConfirmation,
  resetEmailSent,
  setChangeUserStatusFlag,
  userId,
  userRole,
  userOrganizations,
}) => {
  const { userInfo } = useAppSelector(selectAuth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [isOperationDropdownOpen, setOperationDropdownOpen] = useState(false);
  const [key, setKey] = useState(0);
  const [displayEmailModal, setDisplayEmailModal] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localBlob, setLocalBlob] = useState<string | null>(null);
  const [avatarSrcError, setAvatarSrcError] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const avatarSrc = userId && !avatarSrcError ? getUserAvatar(Number(userId)) : null;
  const displayAvatarSrc = localBlob || avatarSrc;

  useEffect(() => {
    return () => {
      if (localBlob) URL.revokeObjectURL(localBlob);
    };
  }, [localBlob]);

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    let processedFile = file;
    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
      try {
        const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.5 });
        processedFile = new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpeg'), { type: 'image/jpeg' });
      } catch (err) {
        displayErrorDetails(`Error converting HEIC to JPEG: ${err}`);
        return;
      }
    }

    setIsUploadingAvatar(true);
    try {
      const payload = { files: [{ filename: processedFile.name, mimetype: processedFile.type }] };
      const res = await registerAvatarMedia(payload);
      const uploadResponse = await fetch(res.files[0].upload_url, {
        method: 'PUT',
        body: processedFile,
        headers: { 'content-type': processedFile.type },
      });
      if (!uploadResponse.ok) throw new Error(`Upload failed: ${uploadResponse.status}`);
      await confirmAvatarMedia(res.files[0].upload_id, Number(userId));
      setLocalBlob(URL.createObjectURL(processedFile));
      setAvatarSrcError(false);
    } catch (err) {
      displayErrorDetails(err);
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isCurrentUserAdmin = useMemo(() => {
    return (
      userInfo.role.name === UserRoleName.Admin ||
      userInfo.role.name === UserRoleName.SuperAdmin ||
      userInfo.role.name === UserRoleName.OrganizationAdmin
    );
  }, [userInfo.role.name]);

  useEffect(() => {
    if (thisUser?.status) {
      setKey((prevKey) => prevKey + 1);
    }
  }, [thisUser?.status]);

  const statusOptions = useMemo(() => {
    if (thisUser) {
      const options = [];
      if (thisUser?.role.name === UserRoleName.Provider && thisUser?.status !== 'Pending') {
        options.push({ id: 'pending', name: 'Pending' });
      }
      if (thisUser?.role.name === UserRoleName.Provider && thisUser?.status === 'Pending') {
        options.push({ id: 'active', name: 'Active' }, { id: 'deactivated', name: 'Deactivated' });
      } else if (thisUser?.status !== 'Deactivated') {
        options.push({ id: 'deactivated', name: 'Deactivated' });
      } else {
        options.push({ id: 'active', name: 'Active' });
      }
      return options;
    }
    return [];
  }, [thisUser?.status, thisUser?.role.name]);

  const getInvitationDomain = (): string => {
    const organization =
      userOrganizations.find((org) => org.id !== 'dentistry-one') ||
      userOrganizations.find((org) => org.id === 'dentistry-one');
    return organization
      ? organization.divisions?.length
        ? organization.divisions[0]!.domain || ''
        : organization.practices[0]!.domain || ''
      : '';
  };

  const inviteUser = async (): Promise<void> => {
    setIsInvitedUser(true);
    try {
      await reinviteUserById(userId || '', { frontend_website: `https://${getInvitationDomain()}` }, userInfo.role.name)
        .then((res) => {
          setThisUser({ ...thisUser, status: res?.status, email: res?.email });
        })
        .catch((error: any) => {
          if (
            error.response.data.message.includes('User has no email') ||
            error.response.data.message.includes === '12209-3'
          ) {
            setDisplayEmailModal(true);
          }
        });
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setKey((prevKey) => prevKey + 1);
    setIsInvitedUser(false);
  };
  const [url] = useLocalStorageState<string>('adminDashboardURL', '/admin/dashboard');

  const handleStatusClick = async (status: string): Promise<void> => {
    if (status === 'deactivated' || status === 'pending') {
      setChangeUserStatusFlag(status);
    } else {
      try {
        const userProfile = await activateUserById(Number(userId));
        setThisUser({ ...thisUser, status: userProfile?.status });
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    }
    setKey((prevKey) => prevKey + 1);
    setOperationDropdownOpen(false);
  };

  useEffect(() => {
    if (thisUser && thisUser) {
      if (
        (userInfo.role.name === UserRoleName.OrganizationAdmin ||
          userInfo.role.name === UserRoleName.Admin ||
          userInfo.role.name === UserRoleName.SuperAdmin) &&
        (thisUser?.role.name === UserRoleName.Patient || thisUser?.role.name === UserRoleName.Dependent)
      ) {
        dispatch(setSelectedPatientByAdmin({ selectedPatient: thisUser }));
        dispatch(
          setSelectedPatient({
            value: thisUser?.id.toString(),
            label: 'Current Patient',
          }),
        );
      }
    }
  }, []);

  const goToDashboard = useCallback(() => {
    return url ? navigate(`/admin/dashboard${url}`) : navigate('/admin/dashboard');
  }, [navigate]);

  const handleButtonClick = (): void => {
    const currentPathname = window.location.pathname;
    const newPathname = `${currentPathname}/create-consultation-for`;
    navigate(newPathname);
  };

  // `shouldDisableCaseCreation` is the disabled-state for the "Create
  // Consultation" button (the variable's name is what its truthy
  // value does, not what its name implies). Two reasons to block:
  //
  //   - Patient is in an inactive status (Deactivated / Not Invited).
  //     This applies to everyone, including admins; you can't open a
  //     new consultation against a closed account.
  //
  //   - Patient has no address yet. This used to apply to admins only
  //     when ALL of the patient's organizations were "restricted"
  //     (non-shared-network, non-dentistry-one), which is a narrow
  //     corner of the admin population. Admins managing patients in
  //     the shared dentistry-one network were left blocked behind the
  //     same check that's meant for patient self-serve. Bypass the
  //     address check for any admin role — admins routinely need to
  //     spin up a consultation while the patient's address is still
  //     being gathered.
  const shouldDisableCaseCreation = useMemo(() => {
    if (['Deactivated', 'Not Invited'].includes(thisUser?.status)) {
      return true;
    }
    if (isCurrentUserAdmin) {
      return false;
    }
    return thisUser?.address === null || thisUser?.address?.state === null;
  }, [isCurrentUserAdmin, thisUser?.status, thisUser?.address]);

  return (
    <>
      <div className={styles.headerTop}>
        <span>
          <section className={styles.backButtonContainer}>
            <button type="button" className={styles.backButton} onClick={() => goToDashboard()}>
              <MdKeyboardArrowLeft />
              Back
            </button>
          </section>
        </span>
        <div className={styles.reviewProfileInvitationStatus}>
          <span className={styles.reviewProfileInvitationStatusText}>User Status:</span>
          <div className={styles.reviewProfileInvitationStatusTag}>{thisUser?.status}</div>
        </div>
      </div>
      {thisUser && (
        <div className={styles.profileInfoSection}>
          <div className={styles.avatarWrapper}>
            <div className={styles.avatarCircle}>
              {displayAvatarSrc ? (
                <img
                  className={styles.avatarImg}
                  src={displayAvatarSrc}
                  alt="avatar"
                  onError={() => {
                    if (!localBlob) setAvatarSrcError(true);
                  }}
                />
              ) : (
                <BsPerson className={styles.avatarPersonIcon} />
              )}
            </div>

            <>
              <button
                type="button"
                className={`fs-unmask ${styles.avatarEditBtn}`}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingAvatar}
                title="Change profile picture"
                aria-label="Change profile picture"
              >
                {isUploadingAvatar ? <FaSpinner className={styles.avatarUploadSpinner} /> : <FiEdit />}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.jfif,.pjpeg,.pjp,.heic"
                hidden
                onChange={handleAvatarFileChange}
              />
            </>
          </div>
          <span className={`fs-exclude ${styles.userName}`}>
            {thisUser.full_name ||
              [thisUser.basic_info?.first_name, thisUser.basic_info?.last_name].filter(Boolean).join(' ')}
          </span>
        </div>
      )}
      <div className={styles.reviewProfileHeader} key={key} data-testid="review-profile-header">
        {thisUser && (
          <div className={styles.reviewProfileHeaderCont}>
            <span className={styles.reviewProfileHeaderButtons}>
              <Button
                type="button"
                className={styles.resetPassword}
                onClick={() => inviteUser()}
                disabled={
                  userRole === 'Dependent' ||
                  ['Active', 'Pending', 'Deactivated'].includes(thisUser?.status) ||
                  isInvitedUser
                }
              >
                <IoIosAddCircleOutline />
                Invite User
              </Button>

              {userRole && userRole !== 'Dependent' && (
                <Button
                  type="button"
                  className={styles.resetPassword}
                  onClick={() => setResetPasswordConfirmation({ isOpen: true, userState: 'Active' })}
                  disabled={['Deactivated', 'Not Invited', 'Invited'].includes(thisUser?.status) || resetEmailSent}
                >
                  <img src={resetPasswordIcon} alt="Reset Password" />
                  Reset Password
                </Button>
              )}
              <div className={styles.updateStatus}>
                <Button
                  type="button"
                  className={styles.deleteAccount}
                  onClick={() => setOperationDropdownOpen(!isOperationDropdownOpen)}
                >
                  <MdOutlineChangeCircle />
                  Update User Status
                </Button>
                {isOperationDropdownOpen && (
                  <ul className={styles.dropdownList}>
                    {statusOptions?.map((status) => (
                      <li key={status.id}>
                        <button type="button" onClick={() => handleStatusClick(status.id)}>
                          {status.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </span>
            {(thisUser?.role.name === UserRoleName.Patient || thisUser?.role.name === UserRoleName.Dependent) && (
              <span className={`${styles.reviewProfileHeaderButtons} ${styles.createCase}`}>
                <Button
                  type="button"
                  className={styles.resetPassword}
                  onClick={handleButtonClick}
                  disabled={shouldDisableCaseCreation}
                >
                  <img className={styles.createCaseIcon} src={createCaseIcon} alt="Create New Case" />
                  Create Consultation
                </Button>
              </span>
            )}
          </div>
        )}
      </div>
      {displayEmailModal && (
        <AddEmailModal
          isOpen={displayEmailModal}
          onClose={() => setDisplayEmailModal(false)}
          userId={userId || ''}
          thisUser={thisUser}
          setThisUser={setThisUser}
          inviteUser={inviteUser}
        />
      )}
    </>
  );
};

ReviewProfileHeader.defaultProps = {
  userId: '',
};

export default ReviewProfileHeader;
